/**
 * Field Visibility Configuration
 * Single source of truth for contract field metadata and visibility logic.
 */

export const CONTRACT_TYPES = ['MSA', 'SOW', 'SLA', 'NDA', 'IRU', 'VENDOR', 'CUSTOMER', 'OTHER'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];

/** Fields that are always visible and cannot be hidden */
export const ALWAYS_VISIBLE = new Set([
  'contract_name',
  'contract_type',
  'counterparty_name',
  'status'
]);

export interface FieldDef {
  key: string;
  label: string;
  /** If set, field is only shown by default for these contract types */
  onlyFor?: ContractType[];
  /** If set, field is hidden by default for these contract types */
  hiddenFor?: ContractType[];
}

export interface FieldSection {
  section: string;
  /** If set, section is only shown by default for these contract types */
  onlyFor?: ContractType[];
  fields: FieldDef[];
  /**
   * Indicates which party this section belongs to.
   * 'hyperlink' sections receive distinct visual styling in the import dialog.
   */
  party?: 'counterparty' | 'hyperlink';
}

export const FIELD_SECTIONS: FieldSection[] = [
  // ── Contract Identity ─────────────────────────────────────────
  {
    section: 'Contract Identity',
    fields: [
      { key: 'contract_name', label: 'Contract Name' },
      { key: 'contract_number', label: 'Contract Number' },
      { key: 'contract_type', label: 'Contract Type' },
      { key: 'status', label: 'Status' },
      { key: 'department', label: 'Department' },
      { key: 'legal_entity', label: 'DBA / Trade Name' },
      { key: 'sow_number', label: 'SOW Number', onlyFor: ['SOW'] },
      { key: 'parent_contract_id', label: 'Parent MSA', onlyFor: ['SOW'] },
    ]
  },

  // ── Counterparty ─────────────────────────────────────────────
  {
    section: 'Counterparty',
    party: 'counterparty',
    fields: [
      { key: 'counterparty_name', label: 'Counterparty Name' },
      { key: 'counterparty_type', label: 'Counterparty Type' },
      { key: 'counterparty_company', label: 'Company Name' },
      { key: 'counterparty_contact_name', label: 'Contact Name' },
      { key: 'counterparty_contact_title', label: 'Contact Title' },
      { key: 'counterparty_contact_email', label: 'Contact Email' },
      { key: 'address', label: 'Address' },
    ]
  },

  // ── Dates & Term ─────────────────────────────────────────────
  {
    section: 'Dates & Term',
    fields: [
      { key: 'effective_date', label: 'Effective Date' },
      { key: 'start_date', label: 'Start Date' },
      { key: 'expiration_date', label: 'Expiration Date' },
      { key: 'end_date', label: 'End Date' },
      { key: 'term_length_months', label: 'Term Length (months)' },
      { key: 'notice_period_days', label: 'Notice Period (days)' },
      { key: 'termination_for_convenience_days', label: 'Termination for Convenience (days)' },
      { key: 'termination_for_cause_days', label: 'Cure Period for Cause (days)' },
    ]
  },

  // ── Renewal ──────────────────────────────────────────────────
  {
    section: 'Renewal',
    fields: [
      { key: 'auto_renews', label: 'Auto-Renews' },
      { key: 'renewal_term_months', label: 'Renewal Term (months)' },
      { key: 'renewal_terms', label: 'Renewal Terms' },
      { key: 'renewal_notes', label: 'Renewal Notes' },
    ]
  },

  // ── Financial ────────────────────────────────────────────────
  {
    section: 'Financial',
    fields: [
      { key: 'contract_value', label: 'Contract Value' },
      { key: 'currency', label: 'Currency' },
      { key: 'mrc', label: 'Monthly Recurring Charge (MRC)', onlyFor: ['IRU', 'SLA', 'VENDOR', 'CUSTOMER', 'MSA'] },
      { key: 'nrc', label: 'Non-Recurring Charge (NRC)', onlyFor: ['IRU', 'SLA', 'VENDOR', 'CUSTOMER', 'MSA'] },
      { key: 'payment_terms_days', label: 'Payment Terms (days)' },
      { key: 'payment_terms', label: 'Payment Terms' },
      { key: 'billing_frequency', label: 'Billing Frequency' },
      { key: 'billing_milestones', label: 'Billing Milestones' },
      { key: 'late_fees_pct', label: 'Late Fees %' },
      { key: 'taxes_pass_through', label: 'Taxes / Pass-Through' },
      { key: 'credits_penalties_refunds', label: 'Credits, Penalties & Refunds' },
    ]
  },

  // ── Legal & Governance ───────────────────────────────────────
  {
    section: 'Legal & Governance',
    fields: [
      { key: 'governing_law', label: 'Governing Law' },
      { key: 'jurisdiction', label: 'Jurisdiction' },
      { key: 'venue_jurisdiction', label: 'Venue / Jurisdiction' },
      { key: 'assignment_rights', label: 'Assignment Rights' },
      { key: 'amendment_requirements', label: 'Amendment Requirements' },
      { key: 'dispute_resolution_method', label: 'Dispute Resolution' },
      { key: 'force_majeure', label: 'Force Majeure Clause' },
      { key: 'parent_guarantee', label: 'Parent Guarantee' },
    ]
  },

  // ── Liability & Risk ─────────────────────────────────────────
  {
    section: 'Liability & Risk',
    fields: [
      { key: 'liability_cap', label: 'Liability Cap ($)' },
      { key: 'liability_cap_multiplier', label: 'Liability Cap (multiplier)' },
      { key: 'indemnification_scope', label: 'Indemnification' },
      { key: 'insurance_requirements', label: 'Insurance Requirements' },
      { key: 'data_classification', label: 'Data Classification' },
    ]
  },

  // ── Confidentiality & Restrictive Covenants ──────────────────
  {
    section: 'Confidentiality',
    fields: [
      { key: 'confidentiality_period_months', label: 'Confidentiality Survival (months)' },
      { key: 'non_compete_months', label: 'Non-Compete (months)', hiddenFor: ['NDA', 'SLA', 'IRU'] },
      { key: 'non_solicit_months', label: 'Non-Solicitation (months)', hiddenFor: ['NDA', 'SLA', 'IRU'] },
    ]
  },

  // ── IRU / Telecom ─────────────────────────────────────────── (IRU only by default)
  {
    section: 'Fiber & Telecom',
    onlyFor: ['IRU'],
    fields: [
      { key: 'fiber_route', label: 'Fiber Route' },
      { key: 'fiber_strand_count', label: 'Fiber Strand Count' },
      { key: 'fiber_type', label: 'Fiber Type' },
      { key: 'bandwidth_gbps', label: 'Bandwidth (Gbps)' },
    ]
  },

  // ── SLA Metrics ──────────────────────────────────────────── (SLA only by default)
  {
    section: 'SLA Metrics',
    onlyFor: ['SLA'],
    fields: [
      { key: 'sla_uptime_pct', label: 'Committed Uptime %' },
      { key: 'sla_response_time_hours', label: 'Response Time SLA (hours)' },
      { key: 'sla_credit_pct', label: 'SLA Credit %' },
    ]
  },

  // ── Counterparty Signatures ───────────────────────────────────
  {
    section: 'Signatures',
    party: 'counterparty',
    fields: [
      { key: 'counterparty_signatory_name', label: 'Counterparty Signatory' },
      { key: 'counterparty_signatory_title', label: 'Counterparty Signatory Title' },
      { key: 'counterparty_signature_date', label: 'Counterparty Signature Date' },
    ]
  },

  // ── HyperLink ─────────────────────────────────────────────────
  {
    section: 'HyperLink Infrastructure',
    party: 'hyperlink',
    fields: [
      { key: 'hyperlink_signatory_name', label: 'Signatory Name' },
      { key: 'hyperlink_signatory_title', label: 'Signatory Title' },
      { key: 'hyperlink_signature_date', label: 'Signature Date' },
    ]
  },

  // ── Description & Scope ──────────────────────────────────────
  {
    section: 'Description & Scope',
    fields: [
      { key: 'description', label: 'Description' },
      { key: 'scope_summary', label: 'Scope Summary' },
      { key: 'key_terms', label: 'Key Terms' },
      { key: 'tags', label: 'Tags' },
    ]
  }
];

export const ALL_FIELD_KEYS = FIELD_SECTIONS.flatMap(s => s.fields.map(f => f.key));

/** Visibility config shape: { [contractType]: { [fieldKey]: boolean } } */
export type VisibilityConfig = Record<string, Record<string, boolean>>;

/** Returns true if a field should be shown for a given contract type */
export function isFieldVisible(config: VisibilityConfig, contractType: string, fieldKey: string): boolean {
  if (ALWAYS_VISIBLE.has(fieldKey)) return true;
  const typeConfig = config[contractType];
  if (!typeConfig) return true; // no config for this type = all visible
  const val = typeConfig[fieldKey];
  return val === undefined ? true : val; // missing key = visible (fail-open)
}

/** Build a default config that respects onlyFor / hiddenFor annotations */
export function buildDefaultConfig(): VisibilityConfig {
  const config: VisibilityConfig = {};
  for (const ct of CONTRACT_TYPES) {
    config[ct] = {};
    for (const section of FIELD_SECTIONS) {
      // If the entire section has onlyFor, hide all its fields for other types
      const sectionVisibleForType = !section.onlyFor || section.onlyFor.includes(ct as ContractType);
      for (const field of section.fields) {
        if (!sectionVisibleForType) {
          config[ct][field.key] = false;
        } else if (field.onlyFor && !field.onlyFor.includes(ct as ContractType)) {
          config[ct][field.key] = false;
        } else if (field.hiddenFor && field.hiddenFor.includes(ct as ContractType)) {
          config[ct][field.key] = false;
        } else {
          config[ct][field.key] = true;
        }
      }
    }
  }
  return config;
}
